import { ChevronDown, RotateCcw } from 'lucide-react'
import IndicatorConfigModal from '@/components/Indicators/IndicatorConfigModal'
import ColorPickerField from '@/components/Indicators/ColorPickerField'
import SliderField from '@/components/Indicators/SliderField'
import ToggleField from '@/components/Indicators/ToggleField'
import TelegramNotificationsSection from '@/components/Indicators/TelegramNotificationsSection'
import {
  QUANTUM_COLORS,
  AVAILABLE_SYMBOLS,
  LINE_WIDTHS,
  PIVOT_LEN_RANGE,
  MIN_TOUCHES_RANGE,
  MAX_CHANNEL_BARS_RANGE,
  CONVERGENCE_MIN_RANGE,
  VOL_CONFIRM_RANGE,
  SL_PADDING_RANGE,
  TOUCH_TOLERANCE_RANGE,
  DEVIATION_MAX_RANGE,
  MIN_CHANNEL_WIDTH_ATR_RANGE,
  resolveConfig,
  resolveColor,
  resetToDefaults,
} from './constants.js'

// Sección acordeón con estado persistido en panelSections (molde bollinger).
function Section({ title, children, sectionKey, config, onChange }) {
  const open = config?.panelSections?.[sectionKey] ?? false
  const toggle = () => onChange({ ...config, panelSections: { ...(config.panelSections || {}), [sectionKey]: !open } })
  return (
    <div className="border border-[var(--hair,rgba(176,141,87,0.35))] rounded-lg overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-2.5 py-2 bg-[var(--paper,#FDFDFA)] hover:bg-black/[0.02] transition-colors"
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--gold,#B08D57)]">{title}</span>
        <ChevronDown size={14} className={`text-[var(--ink-soft,#4A5A52)] transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="px-2.5 py-2 space-y-2 bg-white/50 dark:bg-gray-900/20">
          {children}
        </div>
      )}
    </div>
  )
}

// Chips multi-select (activos favoritos).
function MultiChipSelect({ label, options, values, onChange }) {
  const toggleValue = (v) => {
    const next = values.includes(v) ? values.filter(x => x !== v) : [...values, v]
    onChange(next)
  }
  return (
    <div className="space-y-1">
      <span className="text-xs text-[var(--ink-soft,#4A5A52)]">{label}</span>
      <div className="grid grid-cols-4 gap-1">
        {options.map((opt) => (
          <button
            key={String(opt.value)}
            type="button"
            aria-pressed={values.includes(opt.value)}
            onClick={() => toggleValue(opt.value)}
            className={`px-1 py-1 text-[10px] font-medium rounded border transition-colors ${
              values.includes(opt.value)
                ? 'bg-[var(--paper,#FDFDFA)] border-[var(--gold,#B08D57)] text-[var(--green-deep,#0B3D2E)]'
                : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:bg-slate-50 dark:hover:bg-gray-700'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function ChipSelect({ label, options, value, onChange }) {
  return (
    <div className="space-y-1">
      <span className="text-xs text-[var(--ink-soft,#4A5A52)]">{label}</span>
      <div className="grid grid-cols-3 gap-1">
        {options.map((opt) => (
          <button
            key={String(opt.value)}
            type="button"
            aria-pressed={value === opt.value}
            onClick={() => onChange(opt.value)}
            className={`px-1 py-1 text-[10px] font-medium rounded border transition-colors ${
              value === opt.value
                ? 'bg-[var(--paper,#FDFDFA)] border-[var(--gold,#B08D57)] text-[var(--green-deep,#0B3D2E)]'
                : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:bg-slate-50 dark:hover:bg-gray-700'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  )
}

export default function PatronRupturaQuantumPanel({ config, onChange, onClose, chartSymbol, chartTimeframe }) {
  // Config SIEMPRE resuelta: ninguna clave llega undefined a los campos y
  // cada onChange emite el objeto completo.
  const cfg = resolveConfig(config)

  const update = (key, value) => onChange({ ...cfg, [key]: value })

  // Restablecer valores Quantum: defaults de la config conservando enabled,
  // badgePosition y panelSections (resetToDefaults), con confirmación previa.
  const resetAll = () => {
    if (typeof window !== 'undefined' && !window.confirm('¿Restablecer todos los valores de Patrón de Ruptura Quantum a sus defaults?')) return
    onChange(resetToDefaults(cfg))
  }

  return (
    <IndicatorConfigModal title="Configuración Patrón de Ruptura Quantum" isOpen onClose={onClose}>
      <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">

        <Section title="Visualización" sectionKey="visual" config={cfg} onChange={onChange}>
          <ToggleField label="Líneas del patrón" value={cfg.showPatternLines} onChange={(v) => update('showPatternLines', v)} />
          <ToggleField label="Relleno del canal" value={cfg.showChannelFill} onChange={(v) => update('showChannelFill', v)} />
          <ToggleField label="Etiquetas de señal" value={cfg.showSignalLabels} onChange={(v) => update('showSignalLabels', v)} />
          <ToggleField label="Niveles (entrada/SL/TP)" value={cfg.showLevels} onChange={(v) => update('showLevels', v)} />
          <ToggleField label="Zonas riesgo/beneficio" value={cfg.showZones} onChange={(v) => update('showZones', v)} />
          <ToggleField label="Marcadores de resultado" value={cfg.showOutcomeMarkers} onChange={(v) => update('showOutcomeMarkers', v)} />
          <ToggleField label="Últimas 10 señales (tabla)" value={cfg.showLastSignals} onChange={(v) => update('showLastSignals', v)} />
        </Section>

        {/* Rangos copiados del panel legacy breakout_pattern/Panel.jsx: los
            parámetros del motor no se renombran ni reinterpretan. */}
        <Section title="Parámetros del motor" sectionKey="engine" config={cfg} onChange={onChange}>
          <SliderField label="Pivot len" value={cfg.pivotLen} min={PIVOT_LEN_RANGE.min} max={PIVOT_LEN_RANGE.max} step={1} unit="" onChange={(v) => update('pivotLen', v)} />
          <SliderField label="Min touches" value={cfg.minTouches} min={MIN_TOUCHES_RANGE.min} max={MIN_TOUCHES_RANGE.max} step={1} unit="" onChange={(v) => update('minTouches', v)} />
          <SliderField label="Max channel bars" value={cfg.maxChannelBars} min={MAX_CHANNEL_BARS_RANGE.min} max={MAX_CHANNEL_BARS_RANGE.max} step={10} unit="" onChange={(v) => update('maxChannelBars', v)} />
          <SliderField label="Convergence min" value={cfg.convergenceMin} min={CONVERGENCE_MIN_RANGE.min} max={CONVERGENCE_MIN_RANGE.max} step={0.01} unit="" onChange={(v) => update('convergenceMin', v)} />
          <SliderField label="Vol confirm" value={cfg.volConfirm} min={VOL_CONFIRM_RANGE.min} max={VOL_CONFIRM_RANGE.max} step={0.1} unit="×" onChange={(v) => update('volConfirm', v)} />
          <ToggleField label="Vol contraction" value={cfg.volContraction} onChange={(v) => update('volContraction', v)} />
          <ToggleField label="Momentum filter" value={cfg.momentumFilter} onChange={(v) => update('momentumFilter', v)} />
          <SliderField label="SL padding" value={cfg.slPadding} min={SL_PADDING_RANGE.min} max={SL_PADDING_RANGE.max} step={0.1} unit="× ATR" onChange={(v) => update('slPadding', v)} />
          <SliderField label="Touch tolerance" value={cfg.touchTolerance} min={TOUCH_TOLERANCE_RANGE.min} max={TOUCH_TOLERANCE_RANGE.max} step={0.01} unit="" onChange={(v) => update('touchTolerance', v)} />
          <SliderField label="Deviation max" value={cfg.deviationMax} min={DEVIATION_MAX_RANGE.min} max={DEVIATION_MAX_RANGE.max} step={0.05} unit="" onChange={(v) => update('deviationMax', v)} />
          <SliderField label="Min width" value={cfg.minChannelWidthAtr} min={MIN_CHANNEL_WIDTH_ATR_RANGE.min} max={MIN_CHANNEL_WIDTH_ATR_RANGE.max} step={0.1} unit="× ATR" onChange={(v) => update('minChannelWidthAtr', v)} />
        </Section>

        <Section title="Activos favoritos (opcional)" sectionKey="assets" config={cfg} onChange={onChange}>
          <MultiChipSelect
            label="Limitar el indicador a estos símbolos"
            options={AVAILABLE_SYMBOLS.map(s => ({ value: s, label: s }))}
            values={cfg.assets}
            onChange={(v) => update('assets', v)}
          />
          <p className="text-[10px] text-[var(--ink-soft,#4A5A52)] leading-relaxed">
            Vacío = todos los símbolos de la plataforma.
          </p>
        </Section>

        <Section title="Budget" sectionKey="budget" config={cfg} onChange={onChange}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-[var(--ink,#15211B)]">Budget por señal (USDT)</span>
            <input
              type="number"
              min="0"
              step="any"
              value={cfg.budget ?? ''}
              placeholder="Vacío = solo R"
              onChange={(e) => {
                const n = Number(e.target.value)
                // Vacío o <= 0 → null: sin budget el dashboard muestra el PnL
                // del historial en R; con número > 0, la estimación monetaria.
                update('budget', e.target.value === '' || !Number.isFinite(n) || n <= 0 ? null : n)
              }}
              // Sin spinners nativos (feos en móvil): input limpio con validación numérica.
              className="w-28 px-2 py-1 text-[11px] rounded border border-[var(--hair,rgba(176,141,87,0.35))] bg-white dark:bg-gray-100 text-gray-900 placeholder:text-gray-400 text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>
          <p className="text-[10px] text-[var(--ink-soft,#4A5A52)] leading-relaxed">
            Importe de referencia para estimar el PnL del historial (PnL = Σ R × budget). No ejecuta operaciones.
          </p>
        </Section>

        <Section title="Notificaciones" sectionKey="notifications" config={cfg} onChange={onChange}>
          <ToggleField label="Notificaciones activas" value={cfg.notificationsEnabled} onChange={(v) => update('notificationsEnabled', v)} />
          <ToggleField label="Nueva señal" value={cfg.notifyOnSignal} onChange={(v) => update('notifyOnSignal', v)} />
          <ToggleField label="TP1 alcanzado" value={cfg.notifyOnTP1} onChange={(v) => update('notifyOnTP1', v)} />
          <ToggleField label="TP2 alcanzado" value={cfg.notifyOnTP2} onChange={(v) => update('notifyOnTP2', v)} />
          <ToggleField label="TP3 alcanzado" value={cfg.notifyOnTP3} onChange={(v) => update('notifyOnTP3', v)} />
          <ToggleField label="SL alcanzado" value={cfg.notifyOnSL} onChange={(v) => update('notifyOnSL', v)} />
          {/* El envío real de notificaciones requiere una tarea backend
              (fuera del alcance de este rediseño); aquí solo se persisten las
              preferencias y se gestionan las suscripciones Telegram. */}
          <TelegramNotificationsSection
            indicatorId="patron_ruptura_quantum"
            symbol={chartSymbol}
            timeframe={chartTimeframe}
          />
        </Section>

        <Section title="Colores" sectionKey="colors" config={cfg} onChange={onChange}>
          <ColorPickerField label="Resistencia" value={resolveColor(cfg.resistanceColor, QUANTUM_COLORS.bear)} onChange={(v) => update('resistanceColor', v)} />
          <ColorPickerField label="Soporte" value={resolveColor(cfg.supportColor, QUANTUM_COLORS.bull)} onChange={(v) => update('supportColor', v)} />
          <ColorPickerField label="Stop Loss" value={resolveColor(cfg.slColor, QUANTUM_COLORS.bear)} onChange={(v) => update('slColor', v)} />
          <ColorPickerField label="Take Profit" value={resolveColor(cfg.tpColor, QUANTUM_COLORS.bull)} onChange={(v) => update('tpColor', v)} />
          <ColorPickerField label="Entrada" value={resolveColor(cfg.entryColor, QUANTUM_COLORS.neutral)} onChange={(v) => update('entryColor', v)} />
          <ColorPickerField label="Zonas" value={resolveColor(cfg.zoneColor, QUANTUM_COLORS.accent)} onChange={(v) => update('zoneColor', v)} />
        </Section>

        <Section title="Estilo de línea" sectionKey="lineStyle" config={cfg} onChange={onChange}>
          <ChipSelect
            label="Grosor"
            options={LINE_WIDTHS.map(w => ({ value: w, label: String(w) }))}
            value={cfg.lineWidth}
            onChange={(v) => update('lineWidth', v)}
          />
          <ChipSelect
            label="Estilo TP2"
            options={[
              { value: 'solid', label: 'Sólida' },
              { value: 'dashed', label: 'Punteada' },
            ]}
            value={cfg.tp2LineStyle}
            onChange={(v) => update('tp2LineStyle', v)}
          />
        </Section>

        <button
          onClick={resetAll}
          className="w-full flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium rounded border border-[var(--hair,rgba(176,141,87,0.35))] bg-[var(--paper,#FDFDFA)] text-[var(--green-deep,#0B3D2E)] hover:bg-[var(--gold,#B08D57)] hover:text-white transition-colors"
        >
          <RotateCcw size={12} />
          Restablecer valores Quantum
        </button>

      </div>
    </IndicatorConfigModal>
  )
}
